import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { OfferHistory, ObjectType } from '@/@types/graphql-types';
import { useStreamingOffersHistory } from '@/features/streaming-offers-history/composables/useStreamingOffersHistory';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

type SlimOfferHistory = Omit<OfferHistory, 'package'> & {
	package: Partial<OfferHistory['package']>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'StreamingOffersHistory',
  props: {
    titleName: null,
    titleObjectId: null,
    titleObjectType: null,
    offersHistory: null,
    country: null,
    language: null
  },
  setup(__props: any) {

const props = __props as {
	titleName?: string;
	titleObjectId: number;
	titleObjectType: ObjectType;
	offersHistory: SlimOfferHistory[];
	country: string;
	language: string;
};



const { dateFormatter, spinningText, sortedOffersHistory } = useStreamingOffersHistory({
	offersHistory: () => props.offersHistory,
	titleName: () => props.titleName ?? '',
});

const titleContext = computed(() => new SnowplowTitleContextGraphql(props.titleObjectId, props.titleObjectType));

const onVisibleCallback = {
	callback: (isVisible: boolean) => {
		if (isVisible) {
			TrackingHelper.trackEvent(
				'stream_offer_history_feature',
				{
					action: 'impression',
					label: 'stream_offer_history',
					nonInteraction: true,
				},
				[titleContext.value]
			);
		}
	},
};

const dateBounds = computed(() => {
	const dates = props.offersHistory
		.flatMap(({ dateRanges }) => dateRanges)
		.flatMap(({ start, end }) => [new Date(start).getTime(), new Date(end).getTime()]);

	return {
		earliestStart: new Date(Math.min(...dates)),
		latestEnd: new Date(Math.max(...dates)),
	};
});

function getTooltipDate(date: string, item: SlimOfferHistory) {
	const dateRange = item.dateRanges.find(dateRange => {
		const dates = getDateRanges(dateRange, date);
		return dates.from <= dates.current && dates.to >= dates.current;
	});

	if (!dateRange) return;

	const startDate = dateFormatter.format(new Date(dateRange?.start));
	const endDate = dateFormatter.format(new Date(dateRange?.end));

	return `${startDate} - ${endDate}`;
}

const timelineDates = computed(() => {
	const monthsArray = [];
	const earliestStart = dateBounds.value.earliestStart ?? new Date();
	const latestEnd = dateBounds.value.latestEnd ?? new Date();

	const oneYearAfterLatestEnd = latestEnd;
	oneYearAfterLatestEnd.setDate(1);
	oneYearAfterLatestEnd.setFullYear(oneYearAfterLatestEnd.getFullYear() + 1);

	const currentDateObj = new Date();
	currentDateObj.setDate(1);

	const timelineStart = new Date(earliestStart.getFullYear(), 0, 1);
	timelineStart.setDate(1);
	const timelineEnd = currentDateObj < oneYearAfterLatestEnd ? currentDateObj : oneYearAfterLatestEnd;

	while (timelineStart <= timelineEnd) {
		const month = String(timelineStart.getMonth() + 1).padStart(2, '0');
		const year = timelineStart.getFullYear();
		monthsArray.push(`${month}/${year}`);
		timelineStart.setMonth(timelineStart.getMonth() + 1);
	}

	return monthsArray;
});

function formatTimelineDate(date: string) {
	const month = date.split('/')[0];
	const year = date.split('/')[1];
	return month === '01' ? year : '';
}

function isJan(date: string) {
	return date.split('/')[0] === '01';
}

function isStartOrEndDate(date: string, item: SlimOfferHistory) {
	const dateRanges = item.dateRanges;

	return dateRanges.some(dateRange => {
		const dates = getDateRanges(dateRange, date);
		return dates.from === dates.current || dates.to === dates.current;
	});
}

function isDateInclusive(date: string, item: SlimOfferHistory) {
	const dateRanges = item.dateRanges;

	return dateRanges.some(dateRange => {
		const dates = getDateRanges(dateRange, date);
		return dates.from < dates.current && dates.to > dates.current;
	});
}

function getDateRanges(dateRange: { start: string; end: string }, date: string) {
	const itemDateFrom = dateRange.start.slice(0, 7);
	const itemDateTo = dateRange.end.slice(0, 7);

	const [month, year] = date.split('/');
	const isoString = new Date(Number(year), Number(month) - 1, 1).toISOString().slice(0, 7);

	return {
		current: isoString,
		from: itemDateFrom,
		to: itemDateTo,
	};
}

return { __sfc: true,props, dateFormatter, spinningText, sortedOffersHistory, titleContext, onVisibleCallback, dateBounds, getTooltipDate, timelineDates, formatTimelineDate, isJan, isStartOrEndDate, isDateInclusive, getDateRanges, ProviderIcon }
}

})